import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import Navigation from "../components/Navigation";
import Card from '../components/Card';
import Button from '../components/Buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import { Plus, FileText, Shield, Building, Trash2, RefreshCw } from 'lucide-react';
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import AssignAssessModal from "../components/AssignAssessModal";
import TPRMNewVendorModal from "./TPRMNewVendorModal";
import { useRBAC } from "../hooks/useRBAC";
import { useURLState } from "../hooks/useURLState";
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';

const TPRMApplications = () => {
  const [vendors, setVendors] = useState([]);
  const [applicationsWithRisks, setApplicationsWithRisks] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [vendorIdParam, setVendorIdParam] = useURLState('vendorId');
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);
  const [pageSize, setPageSize] = usePageSize('tprmApplications');
  const totalPages = Math.ceil(totalCount / pageSize);
  const listTopRef = useRef(null);

  const rightPanelRef = useRef(null);
  const fetchIdRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canDeleteAssessments, authVersion } = useRBAC();
  const canDeleteAssessment = canDeleteAssessments(); // super admin only
  const selectedOrgData = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();

  // Reset application state when user/auth changes
  useEffect(() => {
    setApplicationsWithRisks([]);
    setSelectedVendor(null);
  }, [authVersion]);

  useEffect(() => {
    fetchVendors();
  }, [currentPage, pageSize, authVersion]);

  // Auto-select from URL param or navigation state
  useEffect(() => {
    if (vendors.length === 0) return;

    const autoSelectId = vendorIdParam || location.state?.selectedVendorId;
    if (autoSelectId) {
      const targetId = typeof autoSelectId === 'string' ? parseInt(autoSelectId) : autoSelectId;
      const target = vendors.find(v => v.vendorId === targetId);
      if (target) {
        setSelectedVendor(target);
        fetchApplications(target.vendorId);
      }
      if (location.state?.selectedVendorId) {
        navigate('/TPRMApplications', { replace: true });
      }
    }
  }, [vendors]);

  const fetchVendors = async () => {
    const org = JSON.parse(localStorage.getItem("selectedOrganization"));
    if (!org?.organizationId) {
      setLoading(false);
      return;
    }

    setPaging(true);
    try {
      const data = await apiCall("GET", `/TPRMVendor/vendors?page=${currentPage}&pageSize=${pageSize}`);
      setVendors(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setVendors([]);
    } finally {
      setLoading(false);
      setPaging(false);
    }
  };

  const fetchApplications = async (vendorId) => {
    const thisFetchId = ++fetchIdRef.current;
    setApplicationsLoading(true);
    try {
      const [dealApps, buApps] = await Promise.all([
        apiCall("GET", `/applications/vendor/${vendorId}/summary`),
        apiCall("GET", `/businessunitapplications/vendor/${vendorId}/summary`),
      ]);
      if (thisFetchId !== fetchIdRef.current) return;

      const mapDealApp = (app) => ({
        ...app,
        sourceType: 'deal',
        assessmentID: app.assessmentID,
        riskCounts: {
          CRITICAL: app.riskCounts?.critical || 0,
          HIGH: app.riskCounts?.high || 0,
          MEDIUM: app.riskCounts?.medium || 0,
          LOW: app.riskCounts?.low || 0,
        },
        hasAssessment: app.hasAssessment,
        assignedToUserName: app.assignedToUserName,
        assignedToUserId: app.assignedToUserId,
        latestAssessment: app.hasAssessment ? { assessmentID: app.assessmentID, status: app.status } : null,
      });

      const mapBuApp = (app) => ({
        applicationId: app.businessUnitApplicationId,
        applicationName: app.applicationName,
        applicationDescription: app.applicationDescription,
        targetEndDate: app.targetEndDate,
        assessmentID: app.assessmentID,
        hasAssessment: app.hasAssessment,
        assignedToUserName: app.assignedToUserName,
        assignedToUserId: app.assignedToUserId,
        riskCounts: {
          CRITICAL: app.riskCounts?.critical || 0,
          HIGH: app.riskCounts?.high || 0,
          MEDIUM: app.riskCounts?.medium || 0,
          LOW: app.riskCounts?.low || 0,
        },
        totalRisks: app.totalRisks,
        activeRisks: app.activeRisks,
        riskScore: app.riskScore,
        latestAssessment: app.hasAssessment ? { assessmentID: app.assessmentID, status: app.status } : null,
        sourceType: 'businessunit',
        businessUnitApplicationId: app.businessUnitApplicationId,
        businessUnitId: app.businessUnitId,
      });

      setApplicationsWithRisks([
        ...(dealApps || []).map(mapDealApp),
        ...(buApps || []).map(mapBuApp),
      ]);
    } catch (error) {
      if (thisFetchId !== fetchIdRef.current) return;
      setApplicationsWithRisks([]);
    } finally {
      if (thisFetchId === fetchIdRef.current) {
        setApplicationsLoading(false);
      }
    }
  };

  const refreshApplications = async () => {
    if (!selectedVendor) return;
    await fetchApplications(selectedVendor.vendorId);
  };

  const handleVendorCreated = async (newVendor) => {
    setShowNewVendorModal(false);
    setCurrentPageParam(1);
    await fetchVendors();
    // Auto-select the newly created vendor
    if (newVendor?.vendorId) {
      const updatedVendor = { ...newVendor, applicationCount: 0 };
      setSelectedVendor(updatedVendor);
      setApplicationsWithRisks([]);
    }
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      const endpoint = assessmentToDelete.sourceType === 'businessunit'
        ? `/businessunitassessments/${assessmentToDelete.id}`
        : `/assessments/${assessmentToDelete.id}`;
      await apiCall('DELETE', endpoint);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchApplications(selectedVendor.vendorId);
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
        const endpoint = assessmentToReassess.sourceType === 'businessunit'
          ? `/businessunitassessments/${assessmentToReassess.id}/reassess`
          : `/assessments/${assessmentToReassess.id}/reassess`;
        const navPath = assessmentToReassess.sourceType === 'businessunit'
          ? `/businessunitassessment/${assessmentToReassess.id}?step=review`
          : `/assessment/${assessmentToReassess.id}?step=review`;
        await apiCall('POST', endpoint);
        setShowReassessModal(false);
        setAssessmentToReassess(null);
        navigate(navPath);
    } catch (error) {} finally {
        setReassessing(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Vendor Applications</h1>
          <p className="mt-2 text-gray-600">
            View vendors and their associated applications
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Vendors List */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Vendors</h2>
              {/*{canManageRisks && (*/}
              {/*  <Button*/}
              {/*    size="sm"*/}
              {/*    onClick={() => setShowNewVendorModal(true)}*/}
              {/*  >*/}
              {/*    <Plus className="h-4 w-4 mr-2" />*/}
              {/*    New Vendor*/}
              {/*  </Button>*/}
              {/*)}*/}
            </div>

            {vendors.length === 0 ? (
              <div className="text-center py-8">
                <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No vendors found. Create your first vendor.</p>
              </div>
            ) : (
              <div className="space-y-3" ref={listTopRef}>
                {vendors.map((vendor) => (
                    <div
                      key={vendor.vendorId}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        selectedVendor?.vendorId === vendor.vendorId
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedVendor(vendor);
                        setVendorIdParam(vendor.vendorId);
                        fetchApplications(vendor.vendorId);
                        rightPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <h3 className="font-medium text-content-primary">{vendor.vendorName}</h3>
                          {vendor.vendorDomain && (
                            <p className="text-sm text-gray-600">{vendor.vendorDomain}</p>
                          )}
                        </div>
                        <div className="ml-4 text-right">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            {vendor.applicationCount || 0} Apps
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Pagination Controls */}
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPageParam}
              onPageSizeChange={setPageSize}
              scrollTargetRef={listTopRef}
              busy={paging}
            />
          </Card>

          {/* Applications List */}
          <Card ref={rightPanelRef}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Applications</h2>
            </div>

            {selectedVendor ? (
              applicationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : applicationsWithRisks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No applications associated with this vendor.
                </div>
              ) : (
                <div className="space-y-3">
                  {[...applicationsWithRisks]
                    .sort((a, b) => (a.applicationName || '').localeCompare(b.applicationName || ''))
                    .map((application) => (
                      <div
                        key={`${application.sourceType || 'deal'}-${application.applicationId}`}
                        className="p-4 border border-gray-200 rounded-lg hover:border-gray-300"
                      >
                        <div>
                          <h3 className="font-medium text-content-primary">
                            {application.applicationName}
                            <span className="text-xs text-gray-400 ml-2">
                              {application.sourceType === 'businessunit' ? '(Business Unit)' : '(Deal)'}
                            </span>
                          </h3>
                          {application.applicationDescription && (
                            <p className="text-sm text-gray-500 mt-1">{application.applicationDescription}</p>
                          )}
                          {application.targetEndDate && (
                            <p className="text-xs text-gray-500 mt-1">
                              Due: {new Date(application.targetEndDate).toLocaleDateString()}
                            </p>
                          )}
                          {application.assignedToUserName && (
                            <p className="text-xs text-blue-600 mt-1">
                              Assigned to: <span className="font-semibold">{application.assignedToUserName}</span>
                            </p>
                          )}
                        </div>

                        <div className="flex space-x-2 mt-3">
                          {canManageRisks && (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={application.hasAssessment && application.latestAssessment?.status === "completed"}
                              title={application.hasAssessment && application.latestAssessment?.status === "completed" ? "Assessment is completed" : ""}
                              onClick={() => {
                                setSelectedApplication(application);
                                setShowAssignModal(true);
                              }}
                            >
                              <Shield className="h-3 w-3 mr-1" />
                              {application.assignedToUserName ? "Reassign" : "Assign"}
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="secondary"
                            className="px-1 py-1 h-9 text-xs"
                            disabled={
                              !canAssess(application.assignedToUserId) ||
                              (application.hasAssessment && application.latestAssessment?.status === "completed")
                            }
                            title={
                              (application.hasAssessment && application.latestAssessment?.status === "completed" ? "Assessment is completed" : "")
                                || getAssessDisabledReason(application.assignedToUserId)
                                || ""
                            }
                            onClick={() => {
                              if (application.sourceType === 'businessunit') {
                                const path = application.assessmentID
                                  ? `/businessunitassessment/${application.assessmentID}`
                                  : '/businessunitassessment';
                                navigate(path, {
                                  state: {
                                    businessUnitApplicationId: application.businessUnitApplicationId,
                                    applicationName: application.applicationName,
                                    applicationDescription: application.applicationDescription,
                                    businessUnitId: application.businessUnitId,
                                  }
                                });
                              } else {
                                const assessPath = application.assessmentID
                                  ? `/assessment/${application.assessmentID}`
                                  : '/assessment';
                                navigate(assessPath, {
                                  state: {
                                    applicationId: application.applicationId,
                                    applicationName: application.applicationName,
                                    applicationDescription: application.applicationDescription,
                                    dealID: application.dealId,
                                  }
                                });
                              }
                            }}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Assess
                          </Button>

                          {application.hasAssessment && application.assessmentID ? (
                            <Button
                              size="sm"
                              variant="primary"
                              className="px-1 py-1 h-9 text-xs"
                              disabled={application.totalRisks === 0}
                              onClick={() => {
                                const basePath = application.sourceType === 'businessunit'
                                  ? `/businessunitassessment/${application.assessmentID}/risks`
                                  : `/assessment/${application.assessmentID}/risks`;
                                navigate(basePath);
                              }}
                            >
                              <Shield className="h-4 w-4 mr-1" />
                              View Risks ({application.activeRisks ?? application.totalRisks})
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              className="px-1 py-1 h-9 text-xs"
                              disabled
                              title="No assessment data available"
                            >
                              <Shield className="h-4 w-4 mr-1" />
                              No Risks
                            </Button>
                          )}
                          {canManageOrganizationRisks() && application.assessmentID && (
                            <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                              disabled={application.latestAssessment?.status !== 'completed'}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssessmentToReassess({ id: application.assessmentID, sourceType: application.sourceType || 'deal' });
                                setShowReassessModal(true);
                              }}>
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Reassess
                            </Button>
                          )}
                          {canDeleteAssessment && application.assessmentID && (
                            <Button size="sm" variant="danger" className="px-1 py-1 h-9 text-xs"
                              onClick={(e) => { e.stopPropagation(); setAssessmentToDelete({ id: application.assessmentID, sourceType: application.sourceType || 'deal' }); setShowDeleteModal(true); }}>
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>

                        {application.hasAssessment ? (
                          <div className="mt-3">
                            {/* Risk badges */}
                            <div className="flex flex-wrap gap-2">
                              {application.riskCounts.CRITICAL > 0 && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                  {application.riskCounts.CRITICAL} Critical
                                </span>
                              )}
                              {application.riskCounts.HIGH > 0 && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                                  {application.riskCounts.HIGH} High
                                </span>
                              )}
                              {application.riskCounts.MEDIUM > 0 && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                  {application.riskCounts.MEDIUM} Medium
                                </span>
                              )}
                              {application.riskCounts.LOW > 0 && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  {application.riskCounts.LOW} Low
                                </span>
                              )}
                            </div>

                            {/* Risk Score Bar */}
                            <div className="mt-2">
                              <div className="text-xs text-gray-500 mb-1">
                                Risk Score: {application.riskScore || 0}%
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-4">
                                <div
                                  className={`h-4 rounded-full ${
                                    (application.riskScore || 0) <= 40
                                      ? 'bg-green-500'
                                      : (application.riskScore || 0) <= 60
                                        ? 'bg-yellow-500'
                                        : (application.riskScore || 0) <= 80
                                          ? 'bg-orange-500'
                                          : 'bg-red-500'
                                  }`}
                                  style={{ width: `${application.riskScore || 0}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {application.totalRisks || 0} Total Risks
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 text-xs text-gray-500">No Assessment Data</div>
                        )}
                      </div>
                    ))}
                </div>
              )
            ) : (
              <div className="text-center py-8">
                <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Select a vendor to view applications</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Assign Assessment Modal */}
      {showAssignModal && selectedApplication && (
        <AssignAssessModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          applicationId={selectedApplication.sourceType === 'businessunit'
            ? selectedApplication.businessUnitApplicationId
            : selectedApplication.applicationId}
          apiEndpoint={selectedApplication.sourceType === 'businessunit'
            ? `/businessunitapplications/${selectedApplication.businessUnitApplicationId}/assign`
            : undefined}
          refreshData={refreshApplications}
          dealId={selectedApplication.sourceType === 'businessunit'
            ? undefined
            : selectedApplication.dealId}
        />
      )}

      {/* New Vendor Modal */}
      <TPRMNewVendorModal
        isOpen={showNewVendorModal}
        onClose={() => setShowNewVendorModal(false)}
        onVendorCreated={handleVendorCreated}
      />

      <DeleteAssessmentModal isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
        onConfirm={handleDeleteAssessment} isDeleting={deleting} />

      <ReassessModal
        isOpen={showReassessModal}
        onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
        onConfirm={handleReassessAssessment}
        isReassessing={reassessing}
      />
    </div>
  );
};

export default TPRMApplications;
